import { z } from 'zod';
import { economicsInputSchema } from './economics.js';
import { pricingVerdictSchema, segmentedCostBandsSchema } from './pricing.js';
/* ==========================================================================
 *  Admin portal schemas (ADR-0011)
 *
 *  These schemas are intentionally NOT exported from @nutians/api-client.
 *  They live in @nutians/shared so the admin portal (apps/admin) can import
 *  them directly without pulling in the public API contract.
 * ========================================================================== */
export const adminUserRoleSchema = z.enum(['user', 'admin']);
/** Response shape for GET /admin/me. */
export const adminMeSchema = z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string().nullable(),
    role: adminUserRoleSchema,
});
/* ----- AI cost view (ADR-0011 §4.2, GET /admin/cost/ai) ------------------- */
/** Per-step cost breakdown line (from ai_cost_daily or ai_usage). */
export const adminCostStepSchema = z.object({
    step: z.string(),
    calls: z.number().int().nonnegative(),
    inTokens: z.number().int().nonnegative(),
    outTokens: z.number().int().nonnegative(),
    costEur: z.number().nonnegative(),
});
/** Per-(feature × model) cost line. Drives the up/downgrade decision: a step
 *  can route to several models (tier/fallback), and this splits the spend so
 *  the cheapest/most-expensive model per feature is visible. */
export const adminCostStepModelSchema = z.object({
    step: z.string(),
    provider: z.string(),
    model: z.string(),
    calls: z.number().int().nonnegative(),
    inTokens: z.number().int().nonnegative(),
    outTokens: z.number().int().nonnegative(),
    costEur: z.number().nonnegative(),
    /** Blended (input + output) spend per 1k tokens; null when 0 tokens. */
    eurPer1kTokens: z.number().nonnegative().nullable(),
});
/** One day in the daily trend series. */
export const adminCostDaySchema = z.object({
    day: z.string().date(),
    calls: z.number().int().nonnegative(),
    costEur: z.number().nonnegative(),
});
/**
 * Response for GET /admin/cost/ai?month=YYYY-MM.
 *
 * Formulas (ADR-0011 §4.2):
 *   costPerAu = totalCostEur / mau  (MAU = trailing-28d active users)
 *   ceiling   = 0.30 (D-16)
 */
/* ----- Infra cost view (ADR-0011 §4.3, GET/POST /admin/cost/infra) ---------- */
/**
 * AI vendors whose costs are already captured in ai_usage.
 * Entering them in infra_cost_monthly would double-count the pricing verdict.
 */
export const AI_VENDOR_BLOCKLIST = ['anthropic', 'openai', 'mistral'];
/** Body for POST /admin/cost/infra — upsert one vendor line. */
export const upsertInfraCostSchema = z
    .object({
    month: z.string().regex(/^\d{4}-\d{2}$/, 'month must be YYYY-MM format'),
    vendor: z.string().min(1).max(100),
    amountEur: z.number().nonnegative(),
    note: z.string().max(500).optional(),
})
    .refine((v) => !AI_VENDOR_BLOCKLIST.some((n) => v.vendor.toLowerCase().includes(n)), {
    message: 'AI vendor costs (Anthropic, OpenAI, Mistral) are already captured in ai_usage — entering them here would double-count the pricing verdict.',
    path: ['vendor'],
});
/** One vendor line returned in the infra cost response. */
export const adminInfraCostLineSchema = z.object({
    vendor: z.string(),
    amountEur: z.number().nonnegative(),
    note: z.string().nullable(),
    enteredBy: z.string().nullable(),
    createdAt: z.string(),
});
/**
 * Response for GET /admin/cost/infra?month=YYYY-MM.
 *
 * Formulas (ADR-0011 §4.3):
 *   costPerAu = totalAmountEur / mau  (MAU = trailing-28d active users)
 *   ceiling   = 0.20 (ADR-0011 §4.3)
 */
export const adminInfraCostResponseSchema = z.object({
    month: z.string(),
    totalAmountEur: z.number().nonnegative(),
    mau: z.number().int().nonnegative(),
    costPerAu: z.number().nonnegative().nullable(),
    ceilingEur: z.number().nonnegative(),
    aboveCeiling: z.boolean(),
    lines: z.array(adminInfraCostLineSchema),
});
/** Plan-level cost breakdown line (ADR-0012 §7 / M-D1). */
export const adminCostPlanSchema = z.object({
    /** 'free' | 'premium' */
    plan: z.string(),
    /** Total AI cost for users in this plan tier. */
    costEur: z.number().nonnegative(),
    /** Distinct users with AI calls in this plan tier during the month. */
    mau: z.number().int().nonnegative(),
    /** costEur / mau; null when mau = 0. Validates Lite ≈ €0 target (ADR-0012 §5). */
    costPerAu: z.number().nonnegative().nullable(),
});
export const adminChatBatteryEconomicsSchema = z.object({
    /** Reporting window length. T10.5 uses this for cap-hit calibration. */
    windowDays: z.number().int().positive(),
    /** Distinct non-premium users with app activity in the window. */
    freeActiveUsers: z.number().int().nonnegative(),
    /** Distinct free users who emitted chat_battery_emptied in the window. */
    emptiedUsers: z.number().int().nonnegative(),
    /** emptiedUsers / freeActiveUsers; null when denominator is 0. */
    emptyHitRate: z.number().nonnegative().nullable(),
    /** Lite chat turns in ai_cost_daily for the window. */
    liteChatTurns: z.number().int().nonnegative(),
    /** Lite chat COGS in EUR for the window. */
    liteChatCostEur: z.number().nonnegative(),
    /** Lite chat COGS per free active user; null when denominator is 0. */
    liteChatCogsPerAu: z.number().nonnegative().nullable(),
    /** Regression tripwire: users over the computed maximum Lite chat ceiling. */
    abuseTailUsers: z.number().int().nonnegative(),
    /** Per-user turn ceiling used by the tripwire for this window. */
    hardCeilingTurnsPerUser: z.number().int().nonnegative(),
    /** Interaction split (ADR-0028 / T18.4): metered model turns vs. zero-cost structured turns. */
    interactionSplit: z.object({
        /** chat_message_metered events: Lite turns that spent battery (confirms C/R calibration). */
        meteredTurns: z.number().int().nonnegative(),
        /** Total zero-cost structured turns (confirmations + clarifications + affirmation shortcuts). */
        actionTurns: z.number().int().nonnegative(),
        actionConfirmed: z.number().int().nonnegative(),
        actionRejected: z.number().int().nonnegative(),
        clarificationAnswered: z.number().int().nonnegative(),
        affirmationShortcuts: z.number().int().nonnegative(),
    }),
    config: z.object({
        capacity: z.number().int().positive(),
        regenHours: z.number().positive(),
        earnedMax: z.number().int().positive(),
        checkInCredits: z.number().int().positive(),
        referralCreditsMonthlyCap: z.number().int().positive(),
    }),
});
export const adminCostAiResponseSchema = z.object({
    /** YYYY-MM period this report covers. */
    month: z.string(),
    /** Total AI spend in EUR for the month. */
    totalCostEur: z.number().nonnegative(),
    /** Trailing-28d MAU at query time (denominator for cost/AU). */
    mau: z.number().int().nonnegative(),
    /** cost_eur / MAU; null when MAU = 0. */
    costPerAu: z.number().nonnegative().nullable(),
    /** D-16 ceiling: €0.30/AU/month. */
    ceilingEur: z.number().nonnegative(),
    /** True when costPerAu exceeds the ceiling. */
    aboveCeiling: z.boolean(),
    /** Per-step breakdown, sorted by costEur descending. */
    byStep: z.array(adminCostStepSchema),
    /**
     * Per-(step × model) breakdown, sorted by costEur descending. Splits each
     * feature's spend across the models that served it so the portal can judge
     * whether to upgrade (better model) or downgrade (cheaper model) a feature.
     */
    byStepModel: z.array(adminCostStepModelSchema),
    /**
     * Plan-level breakdown (ADR-0012 §7 / M-D1): free vs premium cost/AU.
     * Validates the "Lite ≈ €0/AU" claim from §5 against real data.
     */
    byPlan: z.array(adminCostPlanSchema),
    /** Daily totals (all steps combined), ordered by day ascending. */
    dailyTrend: z.array(adminCostDaySchema),
    /** Split: rows where pricedAsModel IS NOT NULL = projected (Ollama @priceAs);
     *  rows where pricedAsModel IS NULL = actual cloud spend. */
    actualCostEur: z.number().nonnegative(),
    projectedCostEur: z.number().nonnegative(),
    /** ADR-0024 §6 battery economics and calibration readout. */
    chatBattery: adminChatBatteryEconomicsSchema,
});
/* ----- Usage distribution view (ADR-0024 §6, GET /admin/metrics/usage-distribution) -- */
export const adminUsageDistributionStatsSchema = z.object({
    /** Number of users/calls in this distribution. */
    count: z.number().int().nonnegative(),
    min: z.number().nonnegative().nullable(),
    median: z.number().nonnegative().nullable(),
    avg: z.number().nonnegative().nullable(),
    max: z.number().nonnegative().nullable(),
});
export const adminTieredUsageDistributionStatsSchema = z.object({
    overall: adminUsageDistributionStatsSchema,
    free: adminUsageDistributionStatsSchema,
    premium: adminUsageDistributionStatsSchema,
});
export const adminUsageDistributionResponseSchema = z.object({
    /** Caller-requested trailing window, before retention caps are applied. */
    requestedWindowDays: z.number().int().positive(),
    /** Effective trailing window for raw_log and ai_usage based log metrics. */
    windowDays: z.number().int().positive(),
    /** Effective trailing window for chat_message metrics, capped by 90-day retention. */
    messageWindowDays: z.number().int().positive(),
    /** Current chat_message retention horizon. */
    retentionCappedDays: z.number().int().positive(),
    /** True when requestedWindowDays exceeded retentionCappedDays for message-derived stats. */
    messageWindowCapped: z.boolean(),
    /** ISO timestamp for the query upper bound. */
    generatedAt: z.string().datetime(),
    messagesPerUser: adminTieredUsageDistributionStatsSchema,
    tokensPerMessage: z.object({
        input: adminTieredUsageDistributionStatsSchema,
        output: adminTieredUsageDistributionStatsSchema,
    }),
    logsPerUser: adminTieredUsageDistributionStatsSchema,
    tokensPerLogCall: z.object({
        input: adminTieredUsageDistributionStatsSchema,
        output: adminTieredUsageDistributionStatsSchema,
    }),
});
/* ----- Revenue summary view (ADR-0011 §4.5, GET /admin/revenue/summary) ---- */
/**
 * Response for GET /admin/revenue/summary?month=YYYY-MM.
 *
 * All formulas are normative per ADR-0011 §4.5.
 * "paying" = is_active AND environment = 'production'.
 * "production" events only — sandbox events never count toward any metric.
 */
/* ----- Economics baseline (ADR-0011 §3.3 / Phase D — GET /admin/economics/baseline) --- */
/**
 * Whether a baseline value came from a real DB query ('actual') or a
 * default/zero substitution used because no data exists yet ('assumption').
 * Every input to the verdict must be tagged so the UI can annotate it.
 */
export const sourceTagSchema = z.enum(['actual', 'assumption']);
/**
 * Per-field source tags for the full §3.4 EconomicsInput vector.
 * The server returns only 'actual' (real DB data) or 'manual' (no data yet,
 * a sensible placeholder was used). 'overridden' is client-side only — set
 * when the operator edits a field that was originally 'actual'.
 */
export const economicsInputSourceTagsSchema = z.object({
    /** MAU is always from DB (even if 0). */
    mau: z.enum(['actual', 'manual']),
    /** All per-segment AI cost fields share one tag. */
    aiCost: z.enum(['actual', 'manual']),
    /** Segment mix (light/avg/heavy %) is always the fixed 30/50/20 assumption. */
    segmentMix: z.literal('manual'),
    /** Free→paid conversion rate from the RC ledger. */
    conversionRate: z.enum(['actual', 'manual']),
    /** Plan pricing (monthly/annual prices + annual-share %). */
    pricing: z.enum(['actual', 'manual']),
    /** Infra cost total. */
    infra: z.enum(['actual', 'manual']),
    /** Store/RC/payment fees — always default assumptions. */
    fees: z.literal('manual'),
    fixedMonthlyOverheadEur: z.literal('manual'),
    targetGrossMargin: z.literal('manual'),
    targetMonthlyOperatingProfitEur: z.literal('manual'),
    /** Vision cost assumptions — 'actual' when seeded from ai_usage rows, else 'manual'. */
    visionAssumptions: z.enum(['actual', 'manual']).optional(),
    /** Lite chat assumptions — 'actual' when seeded from ai_cost_daily, else 'manual'. */
    liteChatAssumptions: z.enum(['actual', 'manual']).optional(),
    /** Coaching attach assumptions — 'actual' when seeded from upsell funnel events, else 'manual'. */
    coachingAssumptions: z.enum(['actual', 'manual']).optional(),
});
/** Per-field source tags for the economics baseline. */
export const adminEconomicsBaselineTagsSchema = z.object({
    mau: sourceTagSchema,
    segmentation: sourceTagSchema,
    blendedCostPerAu: sourceTagSchema,
    infraPerAu: sourceTagSchema,
    conversionRate: sourceTagSchema,
    arpu: sourceTagSchema,
    storeFee: z.literal('assumption'),
    rcFee: z.literal('assumption'),
    currentPrice: z.literal('assumption'),
});
/**
 * Response for GET /admin/economics/baseline.
 *
 * Assembles MAU, per-user cost segmentation, infra/AU, conversion, and store
 * fees into a live pricing verdict (ADR-0011 §3). Every input is tagged
 * 'actual' (derived from DB rows) or 'assumption' (default used because no
 * data exists yet), so the UI can annotate each figure accurately.
 *
 * Infra/AU is null when infra_cost_monthly has no rows for the current month
 * — the verdict then treats it as zero (pessimistically no infra cost known).
 * Conversion rate is null when no 90-day cohort data exists.
 */
export const adminEconomicsBaselineSchema = z.object({
    /** YYYY-MM period used for the AI-cost and infra-cost queries. */
    period: z.string(),
    /** Trailing-28d MAU used as the active-user denominator for all per-AU ratios. */
    mau: z.number().int().nonnegative(),
    /** Per-user trailing-30d AI costs bucketed into Light / Average / Heavy bands. */
    segmentation: segmentedCostBandsSchema,
    /** Weighted blend: 0.30·light + 0.50·avg + 0.20·heavy (ADR-0011 §3.1). */
    blendedCostPerAu: z.number().nonnegative(),
    /** Infra spend per AU for `period`; null when no infra lines have been entered. */
    infraPerAu: z.number().nonnegative().nullable(),
    /** Trailing-90d conversion rate; null when no signup cohort data exists yet. */
    conversionRate: z.number().nonnegative().nullable(),
    /** Current blended ARPU from live subscriptions; null when no paying users. */
    arpu: z.number().nonnegative().nullable(),
    /** Store fee fraction used for the verdict (default: 0.15 small-business tier). */
    storeFee: z.number().min(0).max(1),
    /** RevenueCat fee fraction layered on top (default: 0.01). */
    rcFee: z.number().min(0).max(1),
    /** net_factor = (1 − storeFee) · (1 − rcFee); the fraction of list price kept. */
    netFactor: z.number(),
    /** Current list price the verdict is evaluated against (€8.99 today). */
    currentPrice: z.number().nonnegative(),
    /** Target gross margin used for targetListPrice (default: 0.80). */
    targetMargin: z.number(),
    /** Assembled verdict — break-even, target price, margin at current price. */
    verdict: pricingVerdictSchema,
    /** Per-field tags: whether each input is a DB actual or a default assumption. */
    sourceTags: adminEconomicsBaselineTagsSchema,
    /**
     * Complete §3.4 EconomicsInput seeded from actuals (ADR-0011 §3.5 "Load actuals").
     * The admin pricing simulator uses this as its default state. Every field is
     * populated — 'manual' defaults substitute when no DB data exists — so
     * computeEconomics() can run end-to-end even in the zero-data state.
     */
    economicsInput: economicsInputSchema,
    /** Per-field source tag for the input vector: 'actual' | 'manual'. */
    inputTags: economicsInputSourceTagsSchema,
    /** ADR-0024 §6 battery economics and calibration readout. */
    chatBattery: adminChatBatteryEconomicsSchema,
});
export const adminRevenueSummarySchema = z.object({
    /** YYYY-MM period this report covers. */
    month: z.string(),
    // ── Paying users (current state) ─────────────────────────────────────────
    /** Count of is_active=true AND environment='production' subscription rows. */
    payingUsers: z.number().int().nonnegative(),
    // ── PRIMARY FUNNEL: trial→paid conversion (ADR-0012 §7 / M-D1) ──────────
    // "Conversion" is redefined as trial-start → first paid charge.
    // Denominator: INITIAL_PURCHASE events with period_type='trial' in the month.
    // Numerator:   subsequent RENEWAL events with period_type='normal' for those users.
    // Target: ≥ 15% (ADR-0012 §2.3 — 15–30% regime at trial→paid, not 4–5%).
    /** INITIAL_PURCHASE events with period_type='trial' in the cohort month (production only). */
    trialStarts: z.number().int().nonnegative(),
    /** Trial starters who then received a RENEWAL with period_type='normal' (trial converted to paid). */
    trialConverted: z.number().int().nonnegative(),
    /**
     * trialConverted / trialStarts (primary conversion metric — ADR-0012 §7).
     * Null when trialStarts = 0.
     */
    trialConversionRate: z.number().nonnegative().nullable(),
    /** ADR-0012 §7 target: ≥ 15% (0.15) — trial→paid regime, not free→paid. */
    trialConversionTarget: z.literal(0.15),
    /** True when trialConversionRate ≥ 0.15. */
    aboveTrialConversionTarget: z.boolean(),
    // ── SECONDARY FUNNEL: signup→paid within 30d (legacy free→paid metric) ───
    /** Users who created their account in the cohort month. */
    cohortSignups: z.number().int().nonnegative(),
    /** Cohort users with INITIAL_PURCHASE ≤ 30 days after user.created_at. */
    cohortConverted: z.number().int().nonnegative(),
    /**
     * cohortConverted / cohortSignups (secondary/legacy metric — kept for comparison).
     * Null when cohortSignups = 0 (no division by zero).
     */
    conversionRate: z.number().nonnegative().nullable(),
    /** Legacy target kept for reference: ≥ 4% (0.04). */
    conversionTarget: z.literal(0.04),
    /** True when conversionRate ≥ 0.04. */
    aboveConversionTarget: z.boolean(),
    // ── True churn ────────────────────────────────────────────────────────────
    /**
     * Active production subscriptions at month start.
     * Derived from subscription_event: users whose latest production event
     * before month start is not EXPIRATION.
     */
    activeAtMonthStart: z.number().int().nonnegative(),
    /** Distinct users with an EXPIRATION event in the month (production only). */
    expirationsInMonth: z.number().int().nonnegative(),
    /**
     * expirationsInMonth / activeAtMonthStart.
     * Null when activeAtMonthStart = 0.
     */
    trueChurnRate: z.number().nonnegative().nullable(),
    /** ADR-0011 §4.5 target: ≤ 6% (0.06). */
    trueChurnTarget: z.literal(0.06),
    /** True when trueChurnRate > 0.06 (churn ceiling exceeded — bad). */
    aboveChurnTarget: z.boolean(),
    // ── Voluntary-churn signal ─────────────────────────────────────────────────
    /** Distinct users with a CANCELLATION event in the month (auto-renew off, not yet lapsed). */
    cancellationsInMonth: z.number().int().nonnegative(),
    // ── MRR ───────────────────────────────────────────────────────────────────
    /**
     * Σ(active production subs × monthly-equiv price).
     * Monthly plan: €8.99 / mo; annual plan: €70 / 12 ≈ €5.83 / mo.
     */
    mrr: z.number().nonnegative(),
    // ── ARPU ─────────────────────────────────────────────────────────────────
    /**
     * MRR / payingUsers; null when payingUsers = 0.
     * Note: annual mix lowers blended ARPU (€5.83 < €8.99) — show after mix.
     */
    arpu: z.number().nonnegative().nullable(),
    /** ADR-0011 §4.5 target: ≥ €7.50. */
    arpuTarget: z.literal(7.5),
    /** True when arpu ≥ 7.5. */
    aboveArpuTarget: z.boolean(),
    // ── Annual share ──────────────────────────────────────────────────────────
    /** Count of active production subscribers on an annual product. */
    annualSubscribers: z.number().int().nonnegative(),
    /**
     * annualSubscribers / payingUsers.
     * Null when payingUsers = 0.
     */
    annualShare: z.number().nonnegative().nullable(),
    /** ADR-0011 §4.5 target: ≥ 40% (0.40). */
    annualShareTarget: z.literal(0.4),
    /** True when annualShare ≥ 0.40. */
    aboveAnnualShareTarget: z.boolean(),
});
//# sourceMappingURL=admin.js.map